/**
 * 2027 redesign switches (playbook §3): one global switch plus one per section.
 * Off = what `main` showed before the section's redesign (its `*Legacy.jsx` copies).
 *
 * Per device, no backend. A section's choice is saved in localStorage by its
 * settings row, by `?ui2027.<key>=1|0`, or for every section at once by
 * `?ui2027=1|0`. Read ONCE at load, so a page is never half old, half new.
 *
 * Profile still runs on its own `profile2027Flag.js` (SCO-2710) until the T1
 * ticket moves it here as the `profile` key.
 *
 * Keep this module import-light: index.jsx reads it during boot.
 */
import { readStoredJson, writeStoredJson } from "../functions/safeStorage";

// null = per-device choices apply. true / false beats every section and hides
// the settings rows: false is the one-line rollback for the whole redesign.
export const UI_2027_FORCE = null;

// default: what a device with no saved choice gets (every section off: the app
// ships with the classic look and the New look pop-up asks, SCO-3065). toggleFor: who sees the settings row, "staff" (allAccessUsers) |
// "everyone" (Phil 2026-10-20) | "nobody". Staff only while prod runs ahead of the store apps (Phil 2026-10-28).
export const UI_2027_SECTIONS = {
  seasons: { default: false, storageKey: "sh27Seasons", toggleFor: "staff" },
  rat: { default: false, storageKey: "sh27Rat", toggleFor: "staff" },
  fac: { default: false, storageKey: "sh27Fac", toggleFor: "staff" },
  fat: { default: false, storageKey: "sh27Fat", toggleFor: "staff" },
  notifications: { default: false, storageKey: "sh27Notifications", toggleFor: "staff" },
  groups: { default: false, storageKey: "sh27Groups", toggleFor: "staff" },
  home: { default: false, storageKey: "sh27Home", toggleFor: "staff" },
};

function readUrlChoices() {
  const choices = {};
  try {
    const url = new URL(window.location.href);
    const all = url.searchParams.get("ui2027");
    let changed = false;
    if (all === "1" || all === "0") {
      Object.keys(UI_2027_SECTIONS).forEach((key) => {
        choices[key] = all === "1";
      });
      url.searchParams.delete("ui2027");
      changed = true;
    }
    // A section param beats the global one on the same URL.
    Object.keys(UI_2027_SECTIONS).forEach((key) => {
      const one = url.searchParams.get(`ui2027.${key}`);
      if (one === "1" || one === "0") {
        choices[key] = one === "1";
        url.searchParams.delete(`ui2027.${key}`);
        changed = true;
      }
    });
    // Dropped so a later reload (the settings toggle) doesn't re-apply them.
    if (changed) window.history.replaceState(window.history.state, "", url.toString());
  } catch (err) {
    /* no URL to read */
  }
  return choices;
}

function readChoices() {
  const fromUrl = UI_2027_FORCE === null ? readUrlChoices() : {};
  const out = {};
  Object.entries(UI_2027_SECTIONS).forEach(([key, section]) => {
    if (UI_2027_FORCE !== null) {
      out[key] = UI_2027_FORCE;
      return;
    }
    if (typeof fromUrl[key] === "boolean") {
      writeStoredJson(section.storageKey, fromUrl[key]);
      out[key] = fromUrl[key];
      return;
    }
    const saved = readStoredJson(section.storageKey, null);
    out[key] = typeof saved === "boolean" ? saved : section.default;
  });
  return out;
}

const LOADED = readChoices();

// The choice this page loaded with. Unknown keys are off.
export function isOn(key) {
  return LOADED[key] === true;
}

// The saved choice now: another webview (the Settings sheet, the New look pop-up) may have changed it since load.
export function savedOn(key) {
  const section = UI_2027_SECTIONS[key];
  if (!section) return false;
  if (UI_2027_FORCE !== null) return UI_2027_FORCE;
  const saved = readStoredJson(section.storageKey, null);
  return typeof saved === "boolean" ? saved : section.default;
}

// Whether the settings form shows this section's row to the given player.
export function showsToggle(key, userID, staffIds) {
  const section = UI_2027_SECTIONS[key];
  if (!section || UI_2027_FORCE !== null || !userID) return false;
  return (
    section.toggleFor === "everyone" ||
    (section.toggleFor === "staff" && (staffIds || []).includes(userID))
  );
}

// Saves only; the caller reloads the page.
export function saveChoice(key, on) {
  const section = UI_2027_SECTIONS[key];
  return section ? writeStoredJson(section.storageKey, !!on) : false;
}

const RELOAD_KEY = "sh27Ui2027Reload";

// Reloads this page when a saved choice no longer matches the one it loaded:
// the native Settings sheet is another webview (same pattern as SCO-2710).
export function followUi2027Choices() {
  if (UI_2027_FORCE !== null) return;
  // The settings page writes the choice itself; it never needs to follow it.
  if (window.location.pathname.endsWith("/player/settings")) return;
  // The native organizer sheets must never reload mid-use.
  if (/\/organizer\/(settings|quick-create)\/?$/.test(window.location.pathname)) return;
  const keys = Object.values(UI_2027_SECTIONS).map((s) => s.storageKey);
  const check = () => {
    const stale = Object.entries(UI_2027_SECTIONS).some(([key, section]) => {
      const saved = readStoredJson(section.storageKey, null);
      return typeof saved === "boolean" && saved !== LOADED[key];
    });
    if (!stale) return;
    try {
      // At most one follow-reload per 5s: a storage read that keeps lagging can't loop.
      const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
      if (Date.now() - last < 5000) return;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch (err) {
      return;
    }
    window.location.reload();
  };
  window.addEventListener("storage", (e) => {
    if (keys.includes(e.key)) check();
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) check();
  });
  window.addEventListener("focus", check);
  window.addEventListener("pageshow", check);
  // Covers a reload that read the old value just before the sheet's write landed.
  setTimeout(check, 1500);
}
